<template>
  <div
    class="a11y-wrap"
    :class="{ 'a11y-wrap--dragging': dragging }"
    :style="dragStyle"
  >
    <!-- Floating trigger button.
         Pointer-down on the FAB starts a drag — dragging the button
         repositions the entire `.a11y-wrap` (so the panel popover
         follows the FAB). Releasing without movement is treated as a
         click and toggles the panel; releasing after movement just
         lands the FAB at its new position. -->
    <button
      class="a11y-fab"
      :class="{ 'a11y-fab--open': open }"
      @pointerdown="onDragStart"
      @click="onFabClick"
      :aria-label="
        open
          ? $t('Components.AccessibilityPanel.closeMenu')
          : $t('Components.AccessibilityPanel.openMenu')
      "
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="dialog"
      type="button"
    >
      <i class="fas fa-universal-access"></i>
      <span v-if="activeFeatureCount > 0" class="a11y-fab__badge">{{
        activeFeatureCount
      }}</span>
    </button>

    <!-- Panel -->
    <transition name="a11y-fade">
      <div
        v-if="open"
        class="a11y-panel"
        role="dialog"
        aria-modal="false"
        :aria-label="$t('Components.AccessibilityPanel.menuLabel')"
        @keydown.escape="open = false"
      >
        <!-- Header -->
        <div class="a11y-panel__header">
          <h3 class="a11y-panel__title">
            <i class="fas fa-universal-access" aria-hidden="true"></i>
            {{ $t('Components.AccessibilityPanel.title') }}
            <span class="a11y-panel__shortcut">(Ctrl + U)</span>
          </h3>
          <button
            class="a11y-panel__close"
            @click="open = false"
            :aria-label="$t('Views.Stage.ParticipantsModal.close')"
            type="button"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Body (scrollable) -->
        <div class="a11y-panel__body">
          <!-- Font scale section -->
          <div class="a11y-section">
            <div class="a11y-section__label">
              {{ $t('Components.AccessibilityPanel.textSize') }}
            </div>
            <div class="a11y-scale-row">
              <button
                class="a11y-scale-btn"
                @click="decreaseFontScale"
                :disabled="fontScale <= minScale + 0.001"
                :aria-label="
                  $t('Components.AccessibilityPanel.decreaseFontSize')
                "
                type="button"
              >
                <i class="fas fa-minus"></i>
              </button>
              <div class="a11y-scale-display">
                <div class="a11y-scale-value">{{ percentage }}%</div>
                <input
                  type="range"
                  class="a11y-scale-slider"
                  :min="minScale"
                  :max="maxScale"
                  :step="stepSize"
                  :value="fontScale"
                  @input="onSliderInput"
                  :aria-label="
                    $t('Components.AccessibilityPanel.fontSizeScale')
                  "
                />
              </div>
              <button
                class="a11y-scale-btn"
                @click="increaseFontScale"
                :disabled="fontScale >= maxScale - 0.001"
                :aria-label="
                  $t('Components.AccessibilityPanel.increaseFontSize')
                "
                type="button"
              >
                <i class="fas fa-plus"></i>
              </button>
            </div>
            <label class="a11y-auto-toggle">
              <input
                type="checkbox"
                :checked="autoAdjustEnabled"
                @change="setAutoAdjustEnabled($event.target.checked)"
              />
              <span class="a11y-auto-toggle__label">
                <i class="fas fa-magic" aria-hidden="true"></i>
                {{ $t('Components.AccessibilityPanel.autoAdjust') }}
              </span>
            </label>
          </div>

          <!-- Feature grid -->
          <div class="a11y-grid">
            <button
              v-for="tile in featureTiles"
              :key="tile.key"
              class="a11y-tile"
              :class="{ 'a11y-tile--active': tile.isActive() }"
              :aria-pressed="tile.isActive() ? 'true' : 'false'"
              @click="tile.onClick()"
              type="button"
            >
              <div class="a11y-tile__icon">
                <i :class="tile.icon" aria-hidden="true"></i>
              </div>
              <div class="a11y-tile__label">{{ tile.label }}</div>
              <div v-if="tile.valueLabel" class="a11y-tile__value">
                {{ tile.valueLabel }}
              </div>
              <span v-if="tile.isActive()" class="a11y-tile__check">
                <i class="fas fa-check"></i>
              </span>
            </button>
          </div>
        </div>

        <!-- Footer: Reset -->
        <div class="a11y-panel__footer">
          <button
            class="a11y-reset-btn"
            @click="resetAll"
            :disabled="activeFeatureCount === 0"
            type="button"
          >
            <i class="fas fa-sync-alt" aria-hidden="true"></i>
            {{ $t('Components.AccessibilityPanel.resetAll') }}
          </button>
        </div>
      </div>
    </transition>

    <!-- Auto-adjust toast -->
    <transition name="a11y-toast">
      <div
        v-if="lastAutoAdjustMessage"
        class="a11y-toast"
        role="status"
        aria-live="polite"
      >
        <div class="a11y-toast__icon">
          <i class="fas fa-magic"></i>
        </div>
        <div class="a11y-toast__body">
          <div class="a11y-toast__title">{{ lastAutoAdjustMessage }}</div>
          <div class="a11y-toast__sub">
            {{ $t('Components.AccessibilityPanel.scaleIsNow', { percentage }) }}
          </div>
        </div>
        <button class="a11y-toast__undo" @click="undoAutoAdjust" type="button">
          {{ $t('Components.AccessibilityPanel.undo') }}
        </button>
        <button
          class="a11y-toast__dismiss"
          @click="dismissToast"
          :aria-label="$t('Components.AccessibilityPanel.dismiss')"
          type="button"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </transition>

    <!-- Keyboard shortcuts legend — visible when Keyboard Nav mode is on -->
    <transition name="a11y-fade">
      <aside
        v-if="features.keyboardNav && legendVisible"
        class="a11y-kbd-legend"
        role="complementary"
        :aria-label="$t('Components.AccessibilityPanel.keyboardShortcuts')"
      >
        <div class="a11y-kbd-legend__header">
          <h4 class="a11y-kbd-legend__title">
            <i class="far fa-keyboard" aria-hidden="true"></i>
            {{ $t('Components.AccessibilityPanel.keyboardShortcuts') }}
          </h4>
          <button
            class="a11y-kbd-legend__close"
            @click="legendVisible = false"
            :aria-label="$t('Components.AccessibilityPanel.hideShortcuts')"
            type="button"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="a11y-kbd-legend__row">
          <span>{{ $t('Components.AccessibilityPanel.openMenu') }}</span>
          <span class="a11y-kbd-legend__keys">
            <kbd class="a11y-kbd-legend__key">Ctrl</kbd>
            <kbd class="a11y-kbd-legend__key">U</kbd>
          </span>
        </div>
        <div class="a11y-kbd-legend__row">
          <span>{{
            $t('Components.AccessibilityPanel.moveFocusForward')
          }}</span>
          <span class="a11y-kbd-legend__keys">
            <kbd class="a11y-kbd-legend__key">Tab</kbd>
          </span>
        </div>
        <div class="a11y-kbd-legend__row">
          <span>{{
            $t('Components.AccessibilityPanel.moveFocusBackward')
          }}</span>
          <span class="a11y-kbd-legend__keys">
            <kbd class="a11y-kbd-legend__key">Shift</kbd>
            <kbd class="a11y-kbd-legend__key">Tab</kbd>
          </span>
        </div>
        <div class="a11y-kbd-legend__row">
          <span>{{
            $t('Components.AccessibilityPanel.activateFocusedItem')
          }}</span>
          <span class="a11y-kbd-legend__keys">
            <kbd class="a11y-kbd-legend__key">Enter</kbd>
          </span>
        </div>
        <div class="a11y-kbd-legend__row">
          <span>{{ $t('Components.AccessibilityPanel.closeDialogMenu') }}</span>
          <span class="a11y-kbd-legend__keys">
            <kbd class="a11y-kbd-legend__key">Esc</kbd>
          </span>
        </div>
        <div class="a11y-kbd-legend__row">
          <span>{{
            $t('Components.AccessibilityPanel.skipToMainContent')
          }}</span>
          <span class="a11y-kbd-legend__keys">
            <kbd class="a11y-kbd-legend__key">Tab</kbd>
            <span style="font-size: 10px; color: #6b7280">{{
              $t('Components.AccessibilityPanel.fromTop')
            }}</span>
          </span>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

const CONTRAST_CYCLE = ['off', 'high', 'dark', 'light'];
const SATURATION_CYCLE = ['normal', 'low', 'high', 'mono'];
const SPACING_CYCLE = ['normal', 'light', 'moderate', 'heavy'];
const LINE_HEIGHT_CYCLE = ['normal', 'tall', 'taller'];
const ALIGN_CYCLE = ['default', 'left', 'center', 'right', 'justify'];

export default {
  name: 'AccessibilityPanel',
  data() {
    return {
      open: false,
      toastTimer: null,
      legendVisible: true,
      // ── Drag state for the floating FAB ──────────────────────
      // Position is stored as left/top in viewport pixels and
      // persisted across sessions in localStorage. `dragging` is
      // true while a pointer drag is in progress; `dragMoved`
      // tracks whether the pointer actually moved past a small
      // threshold so a clean tap still fires `togglePanel`.
      dragging: false,
      dragMoved: false,
      pos: null, // { x, y } in viewport coords, or null = default corner
      dragOffsetX: 0,
      dragOffsetY: 0,
      dragStartX: 0,
      dragStartY: 0
    };
  },
  computed: {
    ...mapGetters('Accessibility', {
      fontScale: 'getFontScale',
      autoAdjustEnabled: 'getAutoAdjustEnabled',
      lastAutoAdjustMessage: 'getLastAutoAdjustMessage',
      features: 'getFeatures',
      activeFeatureCount: 'activeFeatureCount',
      minScale: 'minScale',
      maxScale: 'maxScale',
      stepSize: 'stepSize'
    }),
    percentage() {
      return Math.round(this.fontScale * 100);
    },
    featureTiles() {
      const f = this.features;
      return [
        {
          key: 'contrastPlus',
          label: this.$t('Components.AccessibilityPanel.features.contrastPlus'),
          icon: 'fas fa-adjust',
          isActive: () => f.contrastPlus !== 'off',
          valueLabel: this.labelFor('contrastPlus'),
          onClick: () =>
            this.cycleFeature({ key: 'contrastPlus', values: CONTRAST_CYCLE })
        },
        {
          key: 'highlightLinks',
          label: this.$t(
            'Components.AccessibilityPanel.features.highlightLinks'
          ),
          icon: 'fas fa-link',
          isActive: () => f.highlightLinks,
          onClick: () => this.toggleFeature('highlightLinks')
        },
        {
          key: 'biggerText',
          label: this.$t('Components.AccessibilityPanel.features.biggerText'),
          icon: 'fas fa-text-height',
          isActive: () => f.biggerText,
          onClick: () => this.toggleFeature('biggerText')
        },
        {
          key: 'textSpacing',
          label: this.$t('Components.AccessibilityPanel.features.textSpacing'),
          icon: 'fas fa-arrows-alt-h',
          isActive: () => f.textSpacing !== 'normal',
          valueLabel: this.labelFor('textSpacing'),
          onClick: () =>
            this.cycleFeature({ key: 'textSpacing', values: SPACING_CYCLE })
        },
        {
          key: 'pauseAnimations',
          label: this.$t(
            'Components.AccessibilityPanel.features.pauseAnimations'
          ),
          icon: 'fas fa-pause-circle',
          isActive: () => f.pauseAnimations,
          onClick: () => this.toggleFeature('pauseAnimations')
        },
        {
          key: 'hideImages',
          label: this.$t('Components.AccessibilityPanel.features.hideImages'),
          icon: 'far fa-image',
          isActive: () => f.hideImages,
          onClick: () => this.toggleFeature('hideImages')
        },
        {
          key: 'dyslexiaFriendly',
          label: this.$t(
            'Components.AccessibilityPanel.features.dyslexiaFriendly'
          ),
          icon: 'fas fa-font',
          isActive: () => f.dyslexiaFriendly,
          onClick: () => this.toggleFeature('dyslexiaFriendly')
        },
        {
          key: 'bigCursor',
          label: this.$t('Components.AccessibilityPanel.features.bigCursor'),
          icon: 'fas fa-mouse-pointer',
          isActive: () => f.bigCursor,
          onClick: () => this.toggleFeature('bigCursor')
        },
        {
          key: 'tooltipsVisible',
          label: this.$t(
            'Components.AccessibilityPanel.features.tooltipsVisible'
          ),
          icon: 'far fa-comment-alt',
          isActive: () => f.tooltipsVisible,
          onClick: () => this.toggleFeature('tooltipsVisible')
        },
        {
          key: 'lineHeight',
          label: this.$t('Components.AccessibilityPanel.features.lineHeight'),
          icon: 'fas fa-text-height',
          isActive: () => f.lineHeight !== 'normal',
          valueLabel: this.labelFor('lineHeight'),
          onClick: () =>
            this.cycleFeature({ key: 'lineHeight', values: LINE_HEIGHT_CYCLE })
        },
        {
          key: 'textAlign',
          label: this.$t('Components.AccessibilityPanel.features.textAlign'),
          icon: 'fas fa-align-left',
          isActive: () => f.textAlign !== 'default',
          valueLabel: this.labelFor('textAlign'),
          onClick: () =>
            this.cycleFeature({ key: 'textAlign', values: ALIGN_CYCLE })
        },
        {
          key: 'saturation',
          label: this.$t('Components.AccessibilityPanel.features.saturation'),
          icon: 'fas fa-tint',
          isActive: () => f.saturation !== 'normal',
          valueLabel: this.labelFor('saturation'),
          onClick: () =>
            this.cycleFeature({ key: 'saturation', values: SATURATION_CYCLE })
        },
        {
          key: 'keyboardNav',
          label: this.$t('Components.AccessibilityPanel.features.keyboardNav'),
          icon: 'far fa-keyboard',
          isActive: () => f.keyboardNav,
          onClick: () => this.toggleFeature('keyboardNav')
        },
        {
          key: 'onScreenKeyboard',
          label: this.$t(
            'Components.AccessibilityPanel.features.onScreenKeyboard'
          ),
          icon: 'fas fa-keyboard',
          isActive: () => f.onScreenKeyboard,
          onClick: () => this.toggleFeature('onScreenKeyboard')
        },
        {
          key: 'dwellClick',
          label: this.$t('Components.AccessibilityPanel.features.dwellClick'),
          icon: 'fas fa-hand-pointer',
          isActive: () => f.dwellClick,
          onClick: () => this.toggleFeature('dwellClick')
        },
        {
          key: 'stickyKeys',
          label: this.$t('Components.AccessibilityPanel.features.stickyKeys'),
          icon: 'fas fa-layer-group',
          isActive: () => f.stickyKeys,
          onClick: () => this.toggleFeature('stickyKeys')
        },
        {
          key: 'slowKeys',
          label: this.$t('Components.AccessibilityPanel.features.slowKeys'),
          icon: 'fas fa-stopwatch',
          isActive: () => f.slowKeys,
          onClick: () => this.toggleFeature('slowKeys')
        },
        {
          key: 'typingSuggestions',
          label: this.$t(
            'Components.AccessibilityPanel.features.typingSuggestions'
          ),
          icon: 'fas fa-spell-check',
          isActive: () => f.typingSuggestions,
          onClick: () => this.toggleFeature('typingSuggestions')
        },
        {
          key: 'screenReader',
          label: this.$t('Components.AccessibilityPanel.features.screenReader'),
          icon: 'fas fa-volume-up',
          isActive: () => f.screenReader,
          onClick: () => this.toggleFeature('screenReader')
        }
      ];
    },
    // Inline style applied to `.a11y-wrap` while dragged. Falls
    // back to `null` so the default bottom/right CSS rules win
    // before the user has ever moved the FAB.
    dragStyle() {
      if (!this.pos) return null;
      return {
        left: this.pos.x + 'px',
        top: this.pos.y + 'px',
        right: 'auto',
        bottom: 'auto'
      };
    }
  },
  watch: {
    lastAutoAdjustMessage(val) {
      if (val) {
        if (this.toastTimer) clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => this.dismissToast(), 6000);
      }
    },
    // Re-show the shortcut legend whenever the user (re-)enables keyboard
    // nav, in case they previously dismissed it.
    'features.keyboardNav'(val) {
      if (val) this.legendVisible = true;
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onGlobalHotkey);
    // Click-outside dismisses the panel — the listener is attached
    // on every mount and gates itself on `open` so it doesn't fire
    // for taps that don't matter.
    document.addEventListener('mousedown', this.onDocumentClickOutside, true);
    document.addEventListener('touchstart', this.onDocumentClickOutside, {
      passive: true,
      capture: true
    });
    // Restore the FAB's last-saved position so it stays where the
    // user dropped it across sessions.
    try {
      const raw = localStorage.getItem('tappin_a11y_fab_pos');
      if (raw) {
        const p = JSON.parse(raw);
        if (p && typeof p.x === 'number' && typeof p.y === 'number') {
          this.pos = this.clampToViewport(p.x, p.y);
        }
      }
    } catch (e) {
      /* no-op */
    }
    // Re-clamp on resize so the FAB never escapes the viewport.
    this.fabResizeHandler = () => {
      if (this.pos) {
        this.pos = this.clampToViewport(this.pos.x, this.pos.y);
      }
    };
    window.addEventListener('resize', this.fabResizeHandler);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onGlobalHotkey);
    document.removeEventListener(
      'mousedown',
      this.onDocumentClickOutside,
      true
    );
    document.removeEventListener('touchstart', this.onDocumentClickOutside, {
      passive: true,
      capture: true
    });
    if (this.fabResizeHandler) {
      window.removeEventListener('resize', this.fabResizeHandler);
    }
    this.endDrag();
  },
  methods: {
    ...mapActions('Accessibility', [
      'setFontScale',
      'increaseFontScale',
      'decreaseFontScale',
      'setAutoAdjustEnabled',
      'toggleFeature',
      'cycleFeature',
      'resetAllAccessibility',
      'clearAutoAdjustMessage'
    ]),
    togglePanel() {
      this.open = !this.open;
    },
    // Closes the panel when the user taps anywhere outside the FAB
    // or the panel itself. Both are inside `.a11y-wrap`, so a
    // single contains-check handles both the wrap and the panel
    // (the panel is `position: fixed` but still a DOM descendant).
    onDocumentClickOutside(e) {
      if (!this.open) return;
      const wrap = this.$el;
      const panel = wrap && wrap.querySelector('.a11y-panel');
      const target = e.target;
      if (!target) return;
      if (
        (wrap && wrap.contains(target)) ||
        (panel && panel.contains(target))
      ) {
        return;
      }
      this.open = false;
    },
    // ── Drag handlers ────────────────────────────────────────────
    // The FAB is its own drag handle. Pointer-down arms the drag,
    // pointer-move starts moving it past a small threshold, and
    // pointer-up either commits the new position or — if the user
    // never moved — falls back to a regular click that toggles
    // the panel.
    onFabClick() {
      // Suppress the click that follows a real drag.
      if (this.dragMoved) {
        this.dragMoved = false;
        return;
      }
      this.togglePanel();
    },
    onDragStart(e) {
      // Only the primary button / single touch should start a drag.
      if (e.button !== undefined && e.button !== 0) return;
      const wrap = this.$el;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      this.dragOffsetX = e.clientX - rect.left;
      this.dragOffsetY = e.clientY - rect.top;
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.dragMoved = false;
      // Don't flip `dragging` yet — wait until the pointer actually
      // moves past the threshold. That way a quick tap still routes
      // through the click handler instead of being eaten by drag.
      window.addEventListener('pointermove', this.onDragMove);
      window.addEventListener('pointerup', this.onDragEnd);
      window.addEventListener('pointercancel', this.onDragEnd);
    },
    onDragMove(e) {
      const dx = e.clientX - this.dragStartX;
      const dy = e.clientY - this.dragStartY;
      // 5 px threshold so a tap doesn't accidentally drag.
      if (!this.dragging && Math.hypot(dx, dy) < 5) return;
      if (!this.dragging) {
        this.dragging = true;
        // Panel is anchored to the viewport (not the FAB), so it
        // stays put while the user drags the FAB — no need to
        // auto-close it here.
      }
      this.dragMoved = true;
      const x = e.clientX - this.dragOffsetX;
      const y = e.clientY - this.dragOffsetY;
      this.pos = this.clampToViewport(x, y);
    },
    onDragEnd() {
      this.endDrag();
      // Persist the new position so it sticks across reloads.
      if (this.pos) {
        try {
          localStorage.setItem('tappin_a11y_fab_pos', JSON.stringify(this.pos));
        } catch (e) {
          /* no-op */
        }
      }
    },
    endDrag() {
      this.dragging = false;
      window.removeEventListener('pointermove', this.onDragMove);
      window.removeEventListener('pointerup', this.onDragEnd);
      window.removeEventListener('pointercancel', this.onDragEnd);
    },
    clampToViewport(x, y) {
      const wrap = this.$el;
      const w = wrap ? wrap.offsetWidth : 52;
      const h = wrap ? wrap.offsetHeight : 52;
      const margin = 8;
      const maxX = Math.max(margin, window.innerWidth - w - margin);
      const maxY = Math.max(margin, window.innerHeight - h - margin);
      return {
        x: Math.min(Math.max(margin, x), maxX),
        y: Math.min(Math.max(margin, y), maxY)
      };
    },
    onGlobalHotkey(e) {
      // Ctrl+U (or Cmd+U) toggles the accessibility menu — matches the
      // "Accessibility Menu (CTRL+U)" convention used by UserWay so
      // users with existing muscle memory can reach it instantly.
      // We ignore the hotkey when the user is typing into an input.
      const isTypingField =
        e.target &&
        (e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable);
      if (isTypingField) return;
      if ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U')) {
        e.preventDefault();
        this.togglePanel();
      }
    },
    onSliderInput(e) {
      this.setFontScale(parseFloat(e.target.value));
    },
    resetAll() {
      this.resetAllAccessibility();
    },
    labelFor(key) {
      const value = this.features[key];
      if (
        !value ||
        value === 'off' ||
        value === 'normal' ||
        value === 'default'
      )
        return '';
      // Stored values are identifiers; translate only the display text.
      const path = `Components.AccessibilityPanel.states.${key}.${value}`;
      if (this.$te(path)) return this.$t(path);
      // Fallback: capitalise the first letter for display
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    dismissToast() {
      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
        this.toastTimer = null;
      }
      this.clearAutoAdjustMessage();
    },
    undoAutoAdjust() {
      this.decreaseFontScale();
      this.dismissToast();
    }
  }
};
</script>

<style scoped lang="scss">
.a11y-wrap {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 2147483645; /* above any app modal/backdrop */
  font-family: 'Poppins', sans-serif;

  // While dragging, suppress the panel transition so it doesn't
  // animate position changes mid-drag, and bump the touch-action
  // so mobile browsers don't try to scroll the page instead.
  &--dragging {
    transition: none;
    cursor: grabbing;

    .a11y-fab {
      cursor: grabbing;
      transform: scale(1.05);
    }
  }

  @media (max-width: 640px) {
    bottom: 16px;
    right: 16px;
  }
}

// Pointer-events from the FAB drive drag handling — disable the
// browser's own touch gestures so a finger drag doesn't double up
// as a page scroll.
.a11y-fab {
  touch-action: none;
}

// ── Floating action button ──────────────────────────────────────────
.a11y-fab {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  // Button fill tokens so the FAB matches the event's buttons rather
  // than the contrast-darkened accent.
  background: var(--t-btn, #047873);
  color: var(--t-btn-on, #ffffff);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  box-shadow: 0 4px 12px rgba(4, 120, 115, 0.32), 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, background 0.15s;

  &:hover {
    background: var(--color-primary-hover, #036663);
    transform: scale(1.05);
  }

  &:focus-visible {
    outline: 3px solid #ffffff;
    outline-offset: 2px;
    box-shadow: 0 0 0 5px rgba(4, 120, 115, 0.4);
  }

  &__badge {
    position: absolute;
    top: -3px;
    left: -3px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background: #ef4444;
    color: #ffffff;
    border-radius: 9999px;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #ffffff;
    line-height: 1;
  }
}

// ── Panel ──────────────────────────────────────────────────────────
// `position: fixed` so the panel is anchored to the VIEWPORT, not
// the (draggable) FAB wrapper. No matter where the user has dropped
// the FAB, opening the panel always brings it up at the same
// bottom-right home position.
.a11y-panel {
  position: fixed;
  bottom: 80px;
  right: 24px;
  width: 360px;
  max-height: 82vh;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.22), 0 6px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 2147483645;

  @media (max-width: 640px) {
    bottom: 88px;
    right: 16px;
    left: 16px;
    width: auto;
    max-width: 400px;
    max-height: 78vh;
  }

  // ── Header ─────────────────────────────────────────────────────
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    background: var(--color-primary, #047873);
    color: #ffffff;
    flex-shrink: 0;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;

    i {
      font-size: 18px;
    }
  }

  &__shortcut {
    font-size: 11px;
    font-weight: 500;
    opacity: 0.75;
    letter-spacing: 0.02em;
  }

  &__close {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    transition: background 0.15s;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }

  // ── Body (scrollable) ──────────────────────────────────────────
  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 18px;
  }

  &__footer {
    padding: 14px 18px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
    flex-shrink: 0;
  }
}

// ── Section (text size area) ───────────────────────────────────────
.a11y-section {
  background: #f3f4f6;
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 16px;

  &__label {
    font-size: 11px;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 10px;
  }
}

.a11y-scale-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.a11y-scale-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1.5px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: var(--color-primary, #047873);
    border-color: var(--color-primary, #047873);
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.a11y-scale-display {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.a11y-scale-value {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-primary, #047873);
  line-height: 1;
}

.a11y-scale-slider {
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 9999px;
  background: #d1d5db;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--color-primary, #047873);
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--color-primary, #047873);
    cursor: pointer;
    border: 2px solid #ffffff;
  }
}

.a11y-auto-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 8px 2px 2px;

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
    accent-color: var(--color-primary, #047873);
    cursor: pointer;
  }

  &__label {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 6px;

    i {
      color: var(--color-primary, #047873);
      font-size: 11px;
    }
  }
}

// ── Feature grid ──────────────────────────────────────────────────
.a11y-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.a11y-tile {
  position: relative;
  background: #ffffff;
  border: 1.5px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px 12px 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-family: 'Poppins', sans-serif;
  transition: all 0.15s;

  &:hover {
    border-color: var(--color-primary, #047873);
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &__icon {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #111827;
    transition: color 0.15s;
  }

  &__label {
    font-size: 12px;
    font-weight: 700;
    color: #111827;
    text-align: center;
    line-height: 1.3;
  }

  &__value {
    font-size: 10px;
    font-weight: 600;
    color: var(--color-primary, #047873);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 8px;
    background: #e6f5f4;
    border-radius: 9999px;
  }

  &__check {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--color-primary, #047873);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
  }

  &--active {
    border-color: var(--color-primary, #047873);
    background: #e6f5f4;
    box-shadow: 0 2px 8px rgba(4, 120, 115, 0.15);

    .a11y-tile__icon {
      color: var(--color-primary, #047873);
    }

    &:hover {
      background: #d6efed;
    }
  }
}

// ── Reset button ──────────────────────────────────────────────────
.a11y-reset-btn {
  width: 100%;
  min-height: 44px;
  padding: 11px 16px;
  background: #ffffff;
  color: #6b7280;
  border: 1.5px solid #d1d5db;
  border-radius: 12px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.15s;

  i {
    font-size: 12px;
  }

  &:hover:not(:disabled) {
    background: #fee2e2;
    border-color: #ef4444;
    color: #dc2626;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// ── Toast ──────────────────────────────────────────────────────────
.a11y-toast {
  position: fixed;
  bottom: 20px;
  // FAB sits at the bottom-right; toast slides in just to its left.
  right: 88px;
  max-width: 380px;
  background: #ffffff;
  border-radius: 14px;
  padding: 12px 14px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 2147483644;

  @media (max-width: 640px) {
    left: 16px;
    right: 16px;
    max-width: none;
    bottom: 84px;
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #e6f5f4;
    color: var(--color-primary, #047873);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
  }

  &__sub {
    font-size: 11px;
    color: #6b7280;
    margin-top: 2px;
  }

  &__undo {
    background: none;
    border: none;
    color: var(--color-primary, #047873);
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    flex-shrink: 0;

    &:hover {
      background: #e6f5f4;
    }
  }

  &__dismiss {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    flex-shrink: 0;

    &:hover {
      background: #f3f4f6;
      color: #111827;
    }
  }
}

// ── Transitions ────────────────────────────────────────────────────
.a11y-fade-enter-active,
.a11y-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.a11y-fade-enter,
.a11y-fade-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.96);
}

.a11y-toast-enter-active,
.a11y-toast-leave-active {
  transition: opacity 0.25s, transform 0.25s;
}
.a11y-toast-enter,
.a11y-toast-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
